import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Table, Card } from "react-bootstrap";
import FormGroupTile from "../../../components/core/basic/FormGroupTile";
import FormGroupLabel from "../../../components/core/basic/FormGroupLabel";
import { PRODUCT_AMAZON_LINKS } from "../../../utils/marketplaceConstants";
import { PlanSlider } from "../components/PricingSlider";
import { useFetch } from "../../../hooks/api";
import Loading from "../../../components/core/blocks/Loading";
import { Button } from "../../../components/core/basic/Button";
import { formatCurrency } from "../../../utils/formatNumber";
import api from "../../../utils/api";
import { cut } from "../../../utils/formatText";
import { TbPencilCog } from "react-icons/tb";
import { usePricingPlanUpdate } from "../api/usePricingPlanUpdate";
import { TextField } from "../../../components/fields/base/TextField";
import { Center, Grid, HStack, VStack, Wrap } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { CurrencyInput, NumberInput, OffsetInput } from "../../../components/fields/base/NumberInput";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { ExplainerBox } from "../../../components/core/basic/ExplainerBox";
import { usePricingConfiguration } from "../api/usePricingConfiguration";
import { useCharmPrice } from "../api/useCharmPrice";
import { applyOffset } from "../utils";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { useBulkUpdatePricingPlanStatus } from "../api/useBulkUpdatePricingPlanStatus";
import { keepPreviousData } from "@tanstack/react-query";
import { successToast, errorToast } from "../../../utils/toasts";
import { Accordion } from "../../../components/core/basic/Accordion";
import { useSimpleCan } from "../../../config/casl/casl";
import { useModuleEnabled } from "../../../redux/selectors/modules";

export const ConfigurationSection = ({ pricingPlan, marketPlace }) => {
  const { t } = useTranslation(["pricingv2"]);
  const pricingConfigurationQuery = usePricingConfiguration();
  const b2bDisabled = !pricingConfigurationQuery.data?.send_b2b_price;
  const listPriceDisabled = !pricingConfigurationQuery.data?.send_list_price;
  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");
  const hasPriceDebugModule = useModuleEnabled("PRICE_DEBUG");

  const actionTypeOptions = [
    { label: t(($) => $.pricingv2["Match their price"]), value: "match" },
    { label: t(($) => $.pricingv2["Beat their price"]), value: "decrease" },
    { label: t(($) => $.pricingv2["Premium over their price"]), value: "increase" },
  ];
  const strategyOptions = [
    { label: t(($) => $.pricingv2["Dynamic Pricing"]), value: "dynamic" },
    {
      label: t(($) => $.pricingv2["Best Seller Rank Optimization"]),
      value: "bsr",
      isDisabled: false,
    },
    {
      label: t(($) => $.pricingv2["Competitor Price Matching"]),
      value: "competitor",
      isDisabled: false,
    },
    {
      label: t(($) => $.pricingv2["Rules Engine"]),
      value: "rules_engine",
      isDisabled: false,
    },
  ];

  const getPlanMode = (planMode) => {
    if (planMode < -2) {
      return -2;
    } else if (planMode > 2) {
      return 2;
    }

    return planMode;
  };

  const [planName, setPlanName] = useState(pricingPlan?.plan_name || "");
  const [validationErrors, setValidationErrors] = useState({});
  const [minPrice, setMinPrice] = useState(pricingPlan?.min_price ? parseFloat(pricingPlan?.min_price.toFixed(2)) : 0);
  const [maxPrice, setMaxPrice] = useState(pricingPlan?.max_price ? parseFloat(pricingPlan?.max_price.toFixed(2)) : 0);

  const [overrideB2BOffset, setOverrideB2BOffset] = useState(!!pricingPlan?.b2b_offset_amount);
  const [b2bOffset, setB2bOffset] = useState({
    amount: pricingPlan?.b2b_offset_amount,
    type: pricingPlan?.b2b_offset_type,
  });
  const b2bOffsetResolved =
    b2bDisabled ? { amount: 0, type: "percent" }
    : overrideB2BOffset && b2bOffset ? b2bOffset
    : {
        amount: pricingConfigurationQuery.data?.b2b_offset_amount ?? 0,
        type: pricingConfigurationQuery.data?.b2b_offset_type ?? "percent",
      };
  const b2bPrice = applyOffset(pricingPlan.best_price, b2bOffsetResolved.amount, b2bOffsetResolved.type);

  const [overrideListOffset, setOverrideListOffset] = useState(!!pricingPlan?.list_offset_amount);
  const [listOffset, setListOffset] = useState({
    amount: pricingPlan?.list_offset_amount,
    type: pricingPlan?.list_offset_type,
  });
  const listOffsetResolved =
    listPriceDisabled ? { amount: 0, type: "percent" }
    : overrideListOffset && listOffset ? listOffset
    : {
        amount: pricingConfigurationQuery.data?.list_offset_amount ?? 0,
        type: pricingConfigurationQuery.data?.list_offset_type ?? "percent",
      };
  const listPrice = applyOffset(pricingPlan.best_price, listOffsetResolved.amount, listOffsetResolved.type);

  const [excludeSns, setExcludeSns] = useState(!!pricingPlan?.exclude_sns);
  const [overrideVolume, setOverrideVolume] = useState(pricingPlan?.override_units);
  const [overrideMargin, setOverrideMargin] = useState(pricingPlan?.override_margin);
  const [overrideVolumeValue, setOverrideVolumeValue] = useState(parseInt(pricingPlan?.target_units * 30));
  const [overrideSupply, setOverrideSupply] = useState(pricingPlan?.override_supply);
  const [overrideSupplyValue, setOverrideSupplyValue] = useState(pricingPlan?.target_supply);
  const [targetBSR, setTargetBSR] = useState(pricingPlan?.target_bsr || 1);
  const [currentBSR, setCurrentBSR] = useState(pricingPlan?.target_bsr || 1);
  const [salesNet, setSalesNet] = useState(pricingPlan?.sales_net ?? null);
  const [productList, setProductList] = useState([]);

  const [planMode, setPlanMode] = useState(getPlanMode(pricingPlan?.configured_slider_value));
  const [planStrategy, setPlanStrategy] = useState(strategyOptions[0].value);

  const [competitorList, setCompetitorList] = useState([]);
  const [chosenCompetitor, setChosenCompetitor] = useState(null);
  const [competitorAction, setCompetitorAction] = useState(actionTypeOptions[0]);
  const [competitorValue, setCompetitorValue] = useState(null);
  const [competitorUnit, setCompetitorUnit] = useState("percent");

  const pricingPlanProductsQuery = useFetch(
    ["pricingPlanProducts"],
    `/pricing/pricing_plan_products?pricing_plan_id=${pricingPlan.id}`,
    {},
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );
  const amazonCategory = pricingPlanProductsQuery.data?.[0]?.product.amazon_category ?? "";

  const minCharmQuery = useCharmPrice({ price: minPrice }, { enabled: !!minPrice });
  const maxCharmQuery = useCharmPrice({ price: maxPrice }, { enabled: !!maxPrice });
  const b2bCharmQuery = useCharmPrice({ price: b2bPrice }, { enabled: !!b2bPrice });

  const updatePlanMutation = usePricingPlanUpdate();
  const toggleStatusMutation = useBulkUpdatePricingPlanStatus();

  useEffect(() => {
    if (!pricingPlanProductsQuery.isLoading) {
      const pl = [];
      for (let i = 0; i < pricingPlanProductsQuery.data?.length; i++) {
        const element = pricingPlanProductsQuery.data[i];
        pl.push(element.product.id);
      }
      setProductList(pl);

      if (pl.length === 1) {
        strategyOptions[1].isDisabled = true;
      }

      if (pricingPlan?.current_program?.template_type === "BSR") {
        setTargetBSR(pricingPlan.target_bsr);
        setPlanStrategy(strategyOptions[1].value);
      } else if (pricingPlan?.plan_type === "competitor") {
        setChosenCompetitor(pricingPlan?.competitor_rules[0]?.asin);
        setCompetitorValue(pricingPlan?.competitor_rules[0]?.value);
        setCompetitorUnit(pricingPlan?.competitor_rules[0]?.unit);
        if (pricingPlan?.competitor_rules[0]?.action === "match") {
          setCompetitorAction(actionTypeOptions[0]);
        } else if (pricingPlan?.competitor_rules[0]?.action === "decrease") {
          setCompetitorAction(actionTypeOptions[1]);
        } else if (pricingPlan?.competitor_rules[0]?.action === "increase") {
          setCompetitorAction(actionTypeOptions[2]);
        }
        setPlanStrategy(strategyOptions[2].value);
      } else if (pricingPlan?.is_auto) {
        setPlanStrategy(strategyOptions[0].value);
      } else {
        setPlanStrategy(strategyOptions[3].value);
      }
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingPlanProductsQuery.data, planMode, pricingPlanProductsQuery.isLoading]);

  const getBSR = async (productId) => {
    const res = await api.get(`/pricing/bsr?product_id=${productId}`);

    setCurrentBSR(res.data?.ranks);
    return res.data?.ranks;
  };

  useEffect(() => {
    if (productList.length > 0 && planStrategy === "bsr") {
      getBSR(productList[0]);
    }
  }, [productList, planStrategy]);

  const { data: competitors, loading: loadingCompetitors } = useFetch(
    ["competitors"],
    "/api/competitors",
    {
      marketplace: marketPlace.marketPlace,
      product: productList[0],
    },
    { enabled: productList.length > 0 && planStrategy === "competitor" },
  );

  const asinList = useMemo(() => {
    if (competitors?.data?.results) {
      return competitors?.data?.results?.map((c) => c.product_ref);
    }

    strategyOptions[2].isDisabled = true;

    return [];

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [competitors]);

  useEffect(() => {
    if (asinList && asinList.length > 0 && !loadingCompetitors) {
      const getPrices = async () => {
        const res = await api.post("/merchandise/competitive_price", {
          params: {
            asinList: asinList.join(","),
            marketplace: marketPlace.marketPlace,
          },
        });

        const prices = res.data.prices;
        const cl = [...competitors.data.results];
        for (let i = 0; i < prices.length; i++) {
          for (let j = 0; j < cl.length; j++) {
            if (cl[j].product_ref === prices[i].asin) {
              cl[j].price = prices[i].price;
              break;
            }
          }
        }

        setCompetitorList(cl);
      };

      getPrices();
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [asinList]);

  const updatePlan = async () => {
    const errors = {};
    if (planName.length === 0) {
      errors.planName = t(($) => $.pricingv2["Please enter plan name"]);
    }
    if (minPrice === null || minPrice === "" || parseFloat(minPrice) <= 0) {
      errors.minPrice = t(($) => $.pricingv2["Please enter valid minimum price"]);
    }
    if (maxPrice === null || maxPrice === "" || parseFloat(maxPrice) <= 0) {
      errors.maxPrice = t(($) => $.pricingv2["Please enter valid maximum price"]);
    }
    if (minPrice && maxPrice && parseFloat(minPrice) === parseFloat(maxPrice)) {
      errors.maxPrice = t(($) => $.pricingv2["Maximum price should be greater than minimum price"]);
    }
    if (planStrategy === "bsr") {
      if (targetBSR === null || targetBSR === "" || parseFloat(targetBSR) <= 0) {
        errors.targetBSR = t(($) => $.pricingv2["Please enter valid target BSR"]);
      }
    }

    if (planStrategy === "competitor") {
      if (competitorAction.value !== "match") {
        if (competitorValue === null || competitorValue === "" || parseFloat(competitorValue) <= 0) {
          errors.competitorValue = t(($) => $.pricingv2["Please enter valid value"]);
        }
      }

      if (chosenCompetitor === null) {
        errors.chosenCompetitor = t(($) => $.pricingv2["Please select competitor"]);
      }
    }

    if (overrideVolume && (!overrideVolumeValue || parseFloat(overrideVolumeValue) <= 0)) {
      errors.targetVolume = t(($) => $.pricingv2["Please enter valid target volume"]);
    }
    if (overrideSupply && (!overrideSupplyValue || parseFloat(overrideSupplyValue) <= 0)) {
      errors.supply = t(($) => $.pricingv2["Please enter valid supply target"]);
    }

    if (b2bOffset.amount && b2bOffset.amount > 0) {
      errors.b2bOffset = t(($) => $.pricingv2["B2B offset must be negative to give a discount"]);
    }

    if (listOffset.amount && listOffset.amount < 0) {
      errors.listOffset = t(($) => $.pricingv2["List price should exceed or be equal to offer price"]);
    }

    if (Object.keys(errors).length === 0) {
      let planType = null;
      const competitorRules = [];
      if (planStrategy === "competitor") {
        planType = "competitor";
        competitorRules.push({
          action: competitorAction.value,
          unit: competitorUnit,
          value: parseFloat(competitorValue),
          asin: chosenCompetitor,
        });
      } else if (planStrategy === "bsr") {
        planType = "bsr";
      } else {
        planType = "rules_engine";
      }

      updatePlanMutation.mutate(
        {
          plan_id: pricingPlan.id,
          data: {
            plan_name: planName,
            plan_type: planType,
            configured_slider_value: planMode,
            min_price: parseFloat(minPrice),
            max_price: parseFloat(maxPrice),
            target_bsr: targetBSR,
            competitor_rules: competitorRules,
            override_margin: overrideMargin,
            override_units: overrideVolume,
            target_units: overrideVolumeValue,
            override_supply: overrideSupply,
            target_supply: overrideSupplyValue,
            price_override: pricingPlan.price_override,
            b2b_offset_amount: overrideB2BOffset ? b2bOffset.amount : null,
            b2b_offset_type: overrideB2BOffset ? b2bOffset.type : null,
            list_offset_amount: overrideListOffset ? listOffset.amount : null,
            list_offset_type: overrideListOffset ? listOffset.type : null,
            sales_net: Number.isNaN(salesNet) ? null : salesNet,
            exclude_sns: excludeSns,
            //sales_net: salesNet,
          },
        },
        {
          onSuccess: () => {
            successToast({
              title: t(($) => $.pricingv2["The Pricing Plan was successfully updated."]),
              message: t(($) => $.pricingv2["Any price changes will be reflected in approximately 15 minutes."]),
            });
          },
          onError: (error) => {
            errorToast({
              title: t(($) => $.pricingv2["Error updating pricing plan"]),
              error,
            });
          },
        },
      );
    }

    setValidationErrors(errors);
  };

  if (pricingPlanProductsQuery.isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  const updateButtonStatus = () => {
    if (productList.length === 0) {
      return true;
    }
    if (productList === 0) {
      return true;
    }
    if (updatePlanMutation.isLoading) {
      return true;
    }
    if (currentBSR === null) {
      strategyOptions[1].isDisabled = true;
    }
    if (asinList.length === 0 && planStrategy === "competitor") {
      return true;
    }

    return false;
  };

  const showPricingStrategy = () => {
    if (pricingPlanProductsQuery.isLoading) {
      return false;
    }

    if (loadingCompetitors) {
      return false;
    }

    if (planStrategy === "competitor" && competitorList.length === 0) {
      return false;
    }

    if (planStrategy === "rules_engine" && !pricingPlan.is_auto) {
      return false;
    }

    if (overrideMargin || overrideVolume) {
      return false;
    }

    return true;
  };

  const showPricingStrategyOverride = () => {
    if (pricingPlanProductsQuery.isLoading) {
      return false;
    }

    if (loadingCompetitors) {
      return false;
    }

    if (planStrategy === "competitor" && competitorList.length === 0) {
      return false;
    }

    if (planStrategy === "rules_engine" && !pricingPlan.is_auto) {
      return false;
    }

    if (overrideMargin || overrideVolume) {
      return true;
    }

    return false;
  };

  const showPricingSupplyOverride = () => {
    if (pricingPlanProductsQuery.isLoading) {
      return false;
    }

    if (loadingCompetitors) {
      return false;
    }

    if (planStrategy === "competitor" && competitorList.length === 0) {
      return false;
    }

    if (planStrategy === "rules_engine" && !pricingPlan.is_auto) {
      return false;
    }

    if (overrideSupply) {
      return true;
    }

    return false;
  };

  const strategy = strategyOptions.find((s) => s.value === planStrategy);

  return (
    <VStack alignItems="start" paddingX="medium">
      <div className="my-3" style={{ width: "100%" }}>
        <Card style={{ borderRadius: "0.5rem", backgroundColor: "white" }}>
          <Card.Body className="p-5">
            <HStack width="100%">
              <TextField
                label={t(($) => $.pricingv2["Plan Name - {strategy}"], { strategy: strategy.label })}
                value={planName}
                onChange={(value) => setPlanName(value)}
                errorMessage={validationErrors["planName"]}
                css={{
                  flex: "1",
                  maxWidth: "42%",
                }}
                isDisabled={!userCanUpdatePricingPlans}
              />

              {/* <Select
          label={"Plan Options"}
          selectedKey={planStrategy}
          items={strategyOptions}
          onSelectionChange={(value) => setPlanStrategy(value)}
          isDisabled
        /> */}
              <Button
                variant={pricingPlan.status === "enabled" ? "secondary" : "primary"}
                alignSelf={"end"}
                justifySelf={"end"}
                height="39px"
                isPending={toggleStatusMutation.isLoading}
                isDisabled={!userCanUpdatePricingPlans}
                onPress={() => {
                  if (pricingPlan.status === "enabled") {
                    toggleStatusMutation.mutate({
                      disable: [pricingPlan.id],
                    });
                  } else {
                    toggleStatusMutation.mutate({
                      enable: [pricingPlan.id],
                    });
                  }
                }}
              >
                {pricingPlan.status === "enabled" ?
                  t(($) => $.pricingv2["Disable plan"])
                : t(($) => $.pricingv2["Enable plan"])}
              </Button>
            </HStack>
            <div className="my-3">
              <Wrap gap="medium">
                <CurrencyInput
                  label={t(($) => $.pricingv2["Minimum Price"])}
                  minValue={0}
                  value={minPrice}
                  onChange={(value) => setMinPrice(value)}
                  description={t(($) => $.pricingv2["The charmed minimum price will be {price}"], {
                    price: minCharmQuery.isFetching ? "..." : minCharmQuery.data?.price,
                  })}
                  errorMessage={validationErrors["minPrice"]}
                  isDisabled={!userCanUpdatePricingPlans}
                />
                <CurrencyInput
                  label={t(($) => $.pricingv2["Maximum Price"])}
                  minValue={minPrice}
                  value={maxPrice}
                  onChange={(value) => setMaxPrice(value)}
                  description={t(($) => $.pricingv2["The charmed maximum price will be {price}"], {
                    price: maxCharmQuery.isFetching ? "..." : maxCharmQuery.data?.price,
                  })}
                  errorMessage={validationErrors["maxPrice"]}
                  isDisabled={!userCanUpdatePricingPlans}
                />
                <NumberInput
                  label={t(($) => $.pricingv2["Days To Inventory Depletion"])}
                  value={pricingPlan?.days_of_supply}
                  isDisabled
                  formatOptions={{ maximumFractionDigits: 0 }}
                />
                <LockableNumberInput
                  label={t(($) => $.pricingv2["Days on Hand Target"])}
                  onLockChange={(isLocked) => {
                    setOverrideSupply(isLocked);
                    setPlanMode(pricingPlan?.configured_slider_value);
                  }}
                  isLocked={overrideSupply}
                  value={overrideSupplyValue}
                  onChange={setOverrideSupplyValue}
                  errorMessage={validationErrors["supply"]}
                />
                {hasPriceDebugModule && (
                  <VStack alignItems="start" gap="xsmall">
                    <HStack gap="xsmall" alignItems="center">
                      <span className={css({ fontSize: "large" })}>{t(($) => $.pricingv2["Sales Net"])}</span>
                      <HoverInfo>{t(($) => $.pricingv2["Volume recovery allowance for post-deal periods"])}</HoverInfo>
                    </HStack>
                    <NumberInput
                      value={salesNet}
                      onChange={setSalesNet}
                      minValue={0}
                      formatOptions={{ maximumFractionDigits: 0 }}
                      isDisabled={!userCanUpdatePricingPlans}
                    />
                  </VStack>
                )}
              </Wrap>
            </div>
            <Accordion title={t(($) => $.pricingv2["Advanced Options"])}>
              <div className="my-3">
                <Wrap gap="medium">
                  <VStack gap="xsmall" alignItems={"start"} flex={"1"}>
                    <Checkbox
                      label={t(($) => $.pricingv2["Override B2B Offset"])}
                      value={overrideB2BOffset}
                      onChange={setOverrideB2BOffset}
                      isDisabled={b2bDisabled}
                    />
                    <OffsetInput
                      css={{ width: "100%" }}
                      value={b2bOffsetResolved}
                      onChange={setB2bOffset}
                      isDisabled={b2bDisabled || !overrideB2BOffset}
                      description={
                        b2bDisabled ?
                          <div style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
                            {t(($) => $.pricingv2["Enable in Configuration to offset B2B price"])}
                          </div>
                        : t(($) => $.pricingv2["The charmed B2B price will be {price}"], {
                            price: b2bCharmQuery.isFetching ? "..." : b2bCharmQuery.data?.price,
                          })
                      }
                      errorMessage={validationErrors["b2bOffset"]}
                    />
                  </VStack>

                  <VStack gap="xsmall" alignItems={"start"} flex={"1"}>
                    <Checkbox
                      label={t(($) => $.pricingv2["Override List Price Offset"])}
                      value={overrideListOffset}
                      onChange={setOverrideListOffset}
                      isDisabled={listPriceDisabled}
                    />
                    <OffsetInput
                      css={{ width: "100%" }}
                      value={listOffsetResolved}
                      onChange={setListOffset}
                      isDisabled={listPriceDisabled || !overrideListOffset}
                      description={
                        listPriceDisabled ?
                          <div style={{ overflow: "hidden", whiteSpace: "nowrap" }}>
                            {t(($) => $.pricingv2["Enable in Configuration to offset List price"])}
                          </div>
                        : t(($) => $.pricingv2["List price will be {price}"], { price: listPrice })
                      }
                      errorMessage={validationErrors["listOffset"]}
                    />
                  </VStack>
                </Wrap>
                <VStack gap="xsmall" alignItems={"start"} className={css({ marginTop: "medium" })}>
                  <Checkbox
                    label={t(($) => $.pricingv2["Exclude Subscribe & Save (SNS)"])}
                    value={excludeSns}
                    onChange={setExcludeSns}
                    isDisabled={!userCanUpdatePricingPlans}
                  />
                </VStack>
              </div>
            </Accordion>
            {pricingPlan.relationship && (
              <span>
                <small>
                  {t(($) => $.pricingv2["The minimum price in the pricing plan may be influenced by a relationship."])}
                </small>
              </span>
            )}
          </Card.Body>
        </Card>
      </div>
      {planStrategy === "bsr" && (
        <HStack gap="medium">
          <TextField label={t(($) => $.pricingv2["Category"])} value={amazonCategory} isDisabled />
          <NumberInput
            label={t(($) => $.pricingv2["Current BSR"])}
            value={currentBSR}
            isDisabled
            formatOptions={{ maximumFractionDigits: 0 }}
          />
          <NumberInput
            label={t(($) => $.pricingv2["Target BSR"])}
            value={targetBSR}
            onChange={setTargetBSR}
            ormatOptions={{ maximumFractionDigits: 0 }}
            errorMessage={validationErrors["targetBSR"]}
          />
        </HStack>
      )}
      {planStrategy === "competitor" && (
        <div className="mb-3" style={{ width: "100%" }}>
          <Card style={{ borderRadius: "0.5rem", backgroundColor: "white" }}>
            <Card.Body>
              <Card.Title className="ps-4 pt-4">
                {asinList.length > 0 && competitorList.length === 0 && (
                  <Row className="text-center">
                    <Col xs={12}>
                      <span className="fw-bold pb-3">{t(($) => $.pricingv2["Competitors are loading"])}</span>
                    </Col>
                  </Row>
                )}
                {asinList.length === 0 && (
                  <Row className="text-center">
                    <Col xs={12}>
                      <span className="fw-bold pb-3">
                        {t(($) => $.pricingv2["You have no competitors to price match"])}
                      </span>
                    </Col>
                  </Row>
                )}
                {asinList.length > 0 && (
                  <Row className="text-start">
                    <Col xs={12}>
                      <span className="fw-bold pb-3">{t(($) => $.pricingv2["Competitor List"])}</span>
                    </Col>
                  </Row>
                )}
              </Card.Title>
              <Row className="fs-4 text-center pb-1 px-5">
                <Col xs={12}>
                  {competitorList.length > 0 && (
                    <Table className="table borderless table-hover">
                      <thead>
                        <tr>
                          <th>&nbsp;</th>
                          <th className="text-start">{t(($) => $.pricingv2["ASIN"])}</th>
                          <th className="text-start">{t(($) => $.pricingv2["Title"])}</th>
                          <th className="text-end">{t(($) => $.pricingv2["Price"])}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              checked={chosenCompetitor === "lowest"}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setChosenCompetitor("lowest");
                                } else {
                                  setChosenCompetitor(null);
                                }
                              }}
                            />
                          </td>
                          <td className="text-start">{t(($) => $.pricingv2["N/A"])}</td>
                          <td className="text-start">{t(($) => $.pricingv2["Lowest Price Competitors"])}</td>
                          <td className="text-end">&nbsp;</td>
                        </tr>
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              checked={chosenCompetitor === "highest"}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setChosenCompetitor("highest");
                                } else {
                                  setChosenCompetitor(null);
                                }
                              }}
                            />
                          </td>
                          <td className="text-start">{t(($) => $.pricingv2["N/A"])}</td>
                          <td className="text-start">{t(($) => $.pricingv2["Highest Price Competitors"])}</td>
                          <td className="text-end">&nbsp;</td>
                        </tr>
                        {competitors?.data?.results?.map((competitor, index) => {
                          return (
                            <tr key={index}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={chosenCompetitor === competitor.product_ref}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setChosenCompetitor(competitor.product_ref);
                                    } else {
                                      setChosenCompetitor(null);
                                    }
                                  }}
                                />
                              </td>
                              <td className="text-start">
                                <a
                                  href={`${PRODUCT_AMAZON_LINKS[marketPlace.marketPlace]}${competitor.product_ref}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {competitor.product_ref}
                                </a>
                              </td>
                              <td className="text-start">{cut(competitor.title, 60)}</td>
                              <td className="text-end">
                                {formatCurrency(competitor.price, {
                                  marketPlace: marketPlace.marketPlace,
                                })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}

                  {chosenCompetitor && (
                    <Row className="pb-3 pt-5 mt-3">
                      <Col xs={8} className="text-start">
                        <Row>
                          <Col xs={5} className="text-start">
                            <FormGroupTile
                              label={t(($) => $.pricingv2["I want my price to"])}
                              type="select"
                              multi={false}
                              stateName={"actionType"}
                              handleChildFormElement={(key, value) => {
                                setCompetitorAction(value);
                              }}
                              defaultValue={competitorAction}
                              options={actionTypeOptions}
                            />
                          </Col>
                          <Col xs={4} className="text-center">
                            <FormGroupTile
                              label={
                                competitorUnit === "percent" ?
                                  competitorAction.value === "decrease" ?
                                    t(($) => $.pricingv2["by this percentage or less"])
                                  : t(($) => $.pricingv2["by this percentage or more"])
                                : competitorAction.value === "decrease" ?
                                  t(($) => $.pricingv2["by this amount or less"])
                                : t(($) => $.pricingv2["by this amount or more"])
                              }
                              formControlClass="text-center"
                              type="formcontrol"
                              formControlType={"number"}
                              prefixSide={"left"}
                              disabled={competitorAction.value === "match"}
                              defaultValue={competitorValue ? competitorValue : 0}
                              handleChildFormElement={(key, value) => {
                                setCompetitorValue(value);
                              }}
                            />

                            {validationErrors["competitorValue"] && (
                              <span style={{ color: "red" }}>
                                <small>{validationErrors["competitorValue"]}</small>
                              </span>
                            )}
                          </Col>
                          <Col xs={3} className="text-center">
                            <FormGroupLabel label={t(($) => $.pricingv2["Action Unit"])} titleCase={true} />
                            <Row className="pb-2">
                              <Col xs={3}></Col>
                              <Col xs={6}>
                                <div className="d-flex py-3 align-items-center justify-content-between">
                                  <Checkbox
                                    label="%"
                                    value={competitorUnit === "percent"}
                                    onChange={() => {
                                      setCompetitorUnit("percent");
                                    }}
                                    isDisabled={competitorAction.value === "match"}
                                  />
                                  <Checkbox
                                    label="$"
                                    value={competitorUnit === "currency"}
                                    onChange={() => {
                                      setCompetitorUnit("currency");
                                    }}
                                    isDisabled={competitorAction.value === "match"}
                                  />
                                </div>
                              </Col>
                              <Col xs={3}></Col>
                            </Row>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      )}
      <Card style={{ borderRadius: "0.5rem", backgroundColor: "white" }}>
        <Card.Body className="p-5">
          {planStrategy === "dynamic" && (
            <Grid
              gridTemplateColumns={{
                sm: "minmax(0, 1fr) minmax(0, 2fr)",
                md: "minmax(0, 1fr) minmax(0, 3fr)",
                lg: "minmax(0, 1fr) minmax(0, 5fr)",
              }}
            >
              <VStack alignItems={"stretch"}>
                <LockableNumberInput
                  label={t(($) => $.pricingv2["Target Volume (Monthly)"])}
                  onLockChange={(isLocked) => {
                    setOverrideVolume(isLocked);
                    setOverrideMargin(false);
                    setPlanMode(pricingPlan?.configured_slider_value);
                  }}
                  isLocked={overrideVolume}
                  value={overrideVolumeValue}
                  onChange={(value) => {
                    const originalUnits = pricingPlan?.target_units * 30;
                    if (value < originalUnits) {
                      setPlanMode(2);
                    } else if (value > originalUnits) {
                      setPlanMode(-2);
                    } else {
                      setPlanMode(0);
                    }
                    setOverrideVolumeValue(value || 0);
                  }}
                  errorMessage={validationErrors["targetVolume"]}
                />
              </VStack>
              <VStack alignItems={"start"} className="ps-4">
                {showPricingSupplyOverride() && (
                  <ExplainerBox>
                    {t(
                      ($) =>
                        $.pricingv2[
                          "When the Days of Supply drops below your desired number of days, the dynamic pricing engine will automatically increase prices to manage inventory levels. Once the Days of Supply has recovered, prices will adjust back to the optimal or 'right' price to ensure competitiveness and profitability"
                        ],
                    )}
                  </ExplainerBox>
                )}
                {showPricingStrategyOverride() && (
                  <ExplainerBox>
                    <div>
                      {t(
                        ($) =>
                          $.pricingv2[
                            "Dynamic Pricing adjusts prices to align with your specified target volume or margin goals. It's important to understand that setting an overly ambitious target volume can impact your profit margins. However, a beneficial side effect might include improvements in your organic ranking and Best Seller Rank."
                          ],
                      )}
                    </div>
                    <div>
                      {t(
                        ($) =>
                          $.pricingv2[
                            "Conversely, pushing for a significantly higher Target Margin could potentially harm your organic ranking and Best Seller Rank. To mitigate this risk while aiming to enhance your Target Margin, we suggest a cautious approach of increasing it by only 1% to 5%."
                          ],
                      )}
                    </div>
                  </ExplainerBox>
                )}
                {showPricingStrategy() && (
                  <div className="px-5 pb-5" style={{ width: "100%" }}>
                    <PlanSlider
                      value={planMode}
                      onChange={setPlanMode}
                      isDisabled={overrideMargin || overrideVolume || !userCanUpdatePricingPlans}
                    />
                  </div>
                )}
              </VStack>
            </Grid>
          )}
        </Card.Body>
      </Card>
      <Center width="100%">
        <Button
          onPress={() => updatePlan()}
          isDisabled={updateButtonStatus() || !userCanUpdatePricingPlans}
          isPending={updatePlanMutation.isPending}
        >
          {t(($) => $.pricingv2["Update"])}
        </Button>
      </Center>
    </VStack>
  );
};
function LockableNumberInput({ onLockChange, isLocked, label, ...props }) {
  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");
  return (
    <VStack alignItems="start" gap="xsmall">
      <label
        className={css({
          display: "flex",
          gap: "xsmall",
          fontSize: "large",
        })}
      >
        {label}
        <Button
          variant="contentOnly"
          isDisabled={!userCanUpdatePricingPlans}
          onPress={() => {
            onLockChange(!isLocked);
          }}
        >
          <TbPencilCog className={css({ color: "trellisPalette.0" })} />
        </Button>
      </label>

      {isLocked && <NumberInput {...props} />}

      {!isLocked && <TextField value="-" isDisabled css={{ "& *": { textAlign: "center" } }} />}
    </VStack>
  );
}
